import { useEffect, useState } from "react";
import {
  Phone, PhoneIncoming, PhoneOutgoing, PhoneX, VideoCamera, Users, Clock,
} from "@phosphor-icons/react";
import { useRTC, formatRtcDuration } from "@/context/RTCContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

/**
 * Rich in-chat call history card.
 *
 * Live: polls /api/rtc/room-status every 8s → shows "Join meeting" while
 * the room has participants; flips to "Call ended" (with duration +
 * participants) the moment the last participant leaves.
 *
 * Uses HelloByFlow design tokens only — no dark background, no technical
 * labels visible.
 */
export default function CallCardBubble({ msg, mine, showHeader, avatarBg, avatarFg }) {
  const rtc = useRTC();
  const { user: me } = useAuth();
  const roomId = msg.call_room_id;
  const [status, setStatus] = useState(null);   // { live, ended, participant_count, ended_at }
  const inSameRoom = rtc?.activeRoom?.room_id === roomId;

  // Live room status — every 8s while this bubble is mounted, plus an
  // instant flip when the server broadcasts `hbf:room-closed` for our room
  // (iter130) so stale Join buttons never survive an ended call.
  useEffect(() => {
    if (!roomId) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/rtc/room-status/${encodeURIComponent(roomId)}`);
        if (!cancelled) setStatus(data);
      } catch (_) { /* transient */ }
    };
    poll();
    const iv = setInterval(poll, 8000);
    const onClosed = (e) => {
      if (e?.detail?.room_id !== roomId) return;
      setStatus({ room_id: roomId, live: false, ended: true, ringing: false, participant_count: 0 });
    };
    window.addEventListener("hbf:room-closed", onClosed);
    window.addEventListener("hbf:room-ended",  onClosed);
    return () => {
      cancelled = true;
      clearInterval(iv);
      window.removeEventListener("hbf:room-closed", onClosed);
      window.removeEventListener("hbf:room-ended",  onClosed);
    };
  }, [roomId]);

  const joinCall = async () => {
    if (!rtc || inSameRoom) return;
    const res = await rtc.joinRoom({
      room_id: roomId,
      title: `Call · ${msg.from_user_name || "Teammate"}`,
    });
    if (res?.ok) rtc.expandCall?.();
  };

  const isOutgoing = mine;
  const isEnded    = status?.ended || (status && !status.live && !status.ringing);
  const isLive     = !!status?.live;
  const durationSec = status?.ended_at && msg.created_at
    ? Math.max(0, Math.floor((status.ended_at * 1000 - new Date(msg.created_at).getTime()) / 1000))
    : null;

  const label = isEnded
    ? "Call ended"
    : isLive
      ? "Live meeting"
      : isOutgoing
        ? "Outgoing call"
        : "Incoming call";
  const iconTone = isEnded ? "bg-[var(--hx-line-soft)] text-[var(--hx-ink-3)]"
    : isLive  ? "bg-[#DCFCE7] text-[#166534]"
    : isOutgoing ? "bg-[var(--hx-blue-soft)] text-[var(--hx-blue)]"
    : "bg-[#FEF3C7] text-[#B45309]";
  const IconCmp = isEnded ? PhoneX
    : isLive   ? VideoCamera
    : isOutgoing ? PhoneOutgoing
    : PhoneIncoming;

  const participantCount = Math.max(status?.participant_count || 0, msg.call_invitee_ids?.length || 0);

  return (
    <div className="flex gap-3 px-3 mt-3" data-testid={`team-msg-${msg.id}`}>
      <div className="w-11 shrink-0 pt-1">
        {showHeader && (
          <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-[12px]"
               style={{ background: avatarBg, color: avatarFg }}>
            {(msg.from_user_name || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 max-w-[520px]">
        {showHeader && (
          <div className="flex items-baseline gap-2 mb-1">
            <span className="font-semibold text-[13px]" style={{ color: avatarFg }}>
              {mine ? "You" : (msg.from_user_name || "—")}
            </span>
            <span className="text-[11px] text-[var(--hx-ink-3)]">
              {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
        )}
        <div
          className="rounded-2xl bg-white border border-[var(--hx-line)] p-3.5 flex items-center gap-3 shadow-[0_2px_10px_-6px_rgba(11,21,38,0.08)]"
          data-testid={`call-card-${msg.id}`}
        >
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${iconTone}`}>
            <IconCmp size={20} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] font-bold tracking-[.14em] uppercase text-[var(--hx-ink-3)]">
              {label}
            </div>
            <div className="font-semibold text-[13.5px] text-[var(--hx-ink)] truncate">
              {mine ? "You started a call" : `${msg.from_user_name || "Teammate"} started a call`}
            </div>
            <div className="mt-0.5 flex items-center gap-2 text-[11.5px] text-[var(--hx-ink-3)]">
              <span className="flex items-center gap-1"><Clock size={11} />
                {isEnded && durationSec != null
                  ? formatRtcDuration(durationSec)
                  : new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
              {participantCount > 0 && (
                <>
                  <span className="text-[var(--hx-line)]">·</span>
                  <span className="flex items-center gap-1"><Users size={11} /> {participantCount + 1} participants</span>
                </>
              )}
            </div>
          </div>
          {isEnded ? (
            <span
              className="px-3 py-1.5 rounded-lg text-[11.5px] font-bold bg-[var(--hx-line-soft)] text-[var(--hx-ink-3)]"
              data-testid={`call-card-ended-${msg.id}`}
            >
              Ended
            </span>
          ) : (
            <button
              onClick={joinCall}
              disabled={inSameRoom || (!isLive && status !== null && status?.ended)}
              className={`px-3 py-1.5 rounded-lg text-[12px] font-bold flex items-center gap-1.5 transition-colors ${
                inSameRoom
                  ? "bg-[var(--hx-line)] text-[var(--hx-ink-3)] cursor-not-allowed"
                  : isLive
                    ? "bg-[#10B981] hover:bg-[#059669] text-white"
                    : "bg-[var(--hx-blue)] hover:bg-[var(--hx-blue-deep)] text-white"
              }`}
              data-testid={`call-card-join-${msg.id}`}
            >
              <Phone size={12} weight="fill" />
              {inSameRoom ? "In call" : isLive ? "Join meeting" : "Join"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
